import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    Auth,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User as FirebaseUser,
} from 'firebase/auth';
import { User } from '@bst-stellar/shared';
import { useTenant } from './TenantContext';

interface AuthContextType {
    user: User | null;
    firebaseUser: FirebaseUser | null;
    loading: boolean;
    isPending: boolean; // User account pending admin approval
    authError: string | null; // Authorization error message
    signIn: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const { firebaseConfig, tenant } = useTenant();
    const [user, setUser] = useState<User | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [isPending, setIsPending] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [auth, setAuth] = useState<Auth | null>(null);

    // Initialize Firebase when config is available
    useEffect(() => {
        // Demo mode - no Firebase config
        if (!firebaseConfig) {
            console.log('AuthContext: Running in demo mode (no Firebase)');
            setLoading(false);
            return;
        }

        console.log('AuthContext: Initializing Firebase');
        const app = initializeApp(firebaseConfig);
        const authInstance = getAuth(app);
        setAuth(authInstance);

        // Listen to auth state changes
        const unsubscribe = onAuthStateChanged(authInstance, async (fbUser) => {
            console.log('onAuthStateChanged fired, fbUser:', fbUser?.uid, 'tenant:', tenant?.id);
            setFirebaseUser(fbUser);

            if (fbUser && tenant) {
                // Verify with backend and get user data
                try {
                    const idToken = await fbUser.getIdToken();
                    console.log('Verifying auth with backend, tenantId:', tenant.id);
                    const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ idToken, tenantId: tenant.id }),
                    });

                    console.log('Auth verify response status:', response.status);

                    if (response.ok) {
                        const data = await response.json();
                        console.log('Auth verify response data:', data);

                        // If user is super_admin, they shouldn't be logging in via tenant
                        // Sign them out - they are not authorized for tenant access
                        if (data.user?.role === 'super_admin') {
                            console.log('Super admin detected in tenant context - not authorized for tenant access');
                            await signOut(authInstance);
                            setUser(null);
                            setIsPending(false);
                            setLoading(false);
                            // Generic error message - don't reveal user's role
                            setAuthError('You are not authorized to access this tenant.');
                            return;
                        }

                        setUser(data.user);
                        setIsPending(false);
                    } else if (response.status === 202 || response.status === 403) {
                        const data = await response.json();

                        // Handle pending users
                        if (data.status === 'pending') {
                            console.log('User account pending approval');
                            setIsPending(true);
                            setUser(data.user || null);
                        }
                        // Handle inactive/deactivated users
                        else if (data.status === 'inactive') {
                            console.log('User account is inactive');
                            await signOut(authInstance);
                            setUser(null);
                            setIsPending(false);
                            setLoading(false);
                            setAuthError(data.message || 'Your account has been deactivated');
                            return;
                        }
                    } else {
                        console.error('Auth verify failed with status:', response.status);
                        setUser(null);
                        setIsPending(false);
                    }
                } catch (error) {
                    console.error('Auth verification error:', error);
                    setUser(null);
                    setIsPending(false);
                }
            } else {
                console.log('Setting user to null (fbUser or tenant missing)');
                setUser(null);
                setIsPending(false);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [firebaseConfig, tenant]);

    async function signIn(email: string, password: string) {
        console.log('signIn called with email:', email);
        if (!auth) {
            console.error('Firebase not initialized - auth is null');
            throw new Error('Firebase not initialized');
        }
        console.log('Calling signInWithEmailAndPassword...');
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            console.log('signInWithEmailAndPassword succeeded, user:', result.user.uid);
        } catch (error) {
            console.error('signInWithEmailAndPassword failed:', error);
            throw error;
        }
    }

    async function logout() {
        if (!auth) throw new Error('Firebase not initialized');
        await signOut(auth);
        setUser(null);
        setAuthError(null);
    }

    function clearAuthError() {
        setAuthError(null);
    }

    return (
        <AuthContext.Provider value={{ user, firebaseUser, loading, isPending, authError, signIn, logout, clearAuthError }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
